/**
 * Admin → Questionnaire tab — feature E05-F04 (E05-F04-T03).
 *
 * 1:1 port of the legacy `dashboard.js` kickoff-question UI (renderKickoffQuestions
 * / openKickoffQuestionEditor / saveKickoffQuestion / deleteKickoffQuestion) +
 * `index.html#admin-content-kickoffq` + the kickoff-question dialog. This is the
 * admin Settings tab that LISTS every active `kickoff_questions` row grouped by
 * category and lets an admin create / edit / soft-delete one question.
 *
 * Parity, group-by-group (legacy renderKickoffQuestions):
 *   - questions are GROUPED by `category` in FIRST-APPEARANCE order (a category
 *     enters the order the first time a question references it) — done by the
 *     shared pure `groupKickoffQuestionsByCategory`;
 *   - a question carrying a non-empty `ai_instruction` shows a "hint" badge with
 *     the instruction as its tooltip (title attr) — acceptance T03;
 *   - Edit / Delete actions appear on row hover.
 *
 * Dialog (legacy openKickoffQuestionEditor + saveKickoffQuestion):
 *   - fields: category, text, ai_instruction (optional), sort_order (number);
 *   - category + text are REQUIRED; a blank one blocks the save with the error
 *     toast "Category and text are required" (KQ_REQUIRED_MESSAGE from @eer/shared);
 *   - sort_order parses to an int (default 0) — ordering is by NUMBER, no drag.
 *
 * Transport: questionnaireApi.ts hooks → the shared `apiClient`. A successful
 * create/update/delete invalidates the list so the change re-renders.
 */
import { useMemo, useState } from 'react';
import {
  KQ_REQUIRED_MESSAGE,
  groupKickoffQuestionsByCategory,
  type KickoffQuestionCreateBody,
  type KickoffQuestionDTO,
} from '@eer/shared';
import { Dialog, DialogFooter, useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import {
  useCreateKickoffQuestion,
  useDeleteKickoffQuestion,
  useKickoffQuestions,
  useUpdateKickoffQuestion,
} from './questionnaireApi.js';

const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';

/** Mutable editor-form state (mirrors the legacy kickoff-question dialog fields). */
interface QuestionForm {
  category: string;
  text: string;
  aiInstruction: string;
  sortOrder: string;
}

/** Seed the form from an existing question (edit), or blank values (create). */
function formFromQuestion(q: KickoffQuestionDTO | null): QuestionForm {
  return {
    category: q?.category ?? '',
    text: q?.text ?? '',
    aiInstruction: q?.ai_instruction ?? '',
    sortOrder: q?.sort_order != null ? String(q.sort_order) : '0',
  };
}

/**
 * Build the create/update body from the form. `sort_order`: parseInt, falsy → 0
 * (legacy `parseInt(...) || 0`). `ai_instruction` trimmed (empty allowed).
 */
function bodyFromForm(form: QuestionForm): KickoffQuestionCreateBody {
  const parsed = Number.parseInt(form.sortOrder, 10);
  return {
    category: form.category.trim(),
    text: form.text.trim(),
    ai_instruction: form.aiInstruction.trim(),
    sort_order: Number.isFinite(parsed) ? parsed : 0,
  };
}

function labelledField(id: string, label: string, control: React.JSX.Element): React.JSX.Element {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 block text-xs font-medium text-text-strong">{label}</span>
      {control}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Editor dialog
// ---------------------------------------------------------------------------

interface QuestionDialogProps {
  readonly question: KickoffQuestionDTO | null;
  readonly onClose: () => void;
}

function QuestionDialog({ question, onClose }: QuestionDialogProps): React.JSX.Element {
  const toast = useToast();
  const createMut = useCreateKickoffQuestion();
  const updateMut = useUpdateKickoffQuestion();
  const [form, setForm] = useState<QuestionForm>(() => formFromQuestion(question));
  const isEdit = question !== null;
  const pending = createMut.isPending || updateMut.isPending;

  function patch<K extends keyof QuestionForm>(key: K, value: QuestionForm[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave(): void {
    const body = bodyFromForm(form);
    // category + text required (legacy "Category and text are required").
    if (body.category.length === 0 || body.text.length === 0) {
      toast.error(KQ_REQUIRED_MESSAGE);
      return;
    }
    const onSuccess = (): void => {
      toast.success('Question saved');
      onClose();
    };
    const onError = (error: unknown): void => {
      toast.error(errorMessage(error, 'Save error'));
    };
    if (isEdit && question) {
      updateMut.mutate({ id: question.id, body }, { onSuccess, onError });
    } else {
      createMut.mutate(body, { onSuccess, onError });
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? 'Edit question' : 'Add question'}
      maxWidth="lg"
      testId="kickoffq-dialog"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel="Save"
          confirmDisabled={pending}
          testId="kickoffq-dialog-footer"
        />
      }
    >
      <div className="grid gap-4">
        {labelledField(
          'kickoffq-field-category',
          'Category',
          <input
            id="kickoffq-field-category"
            data-testid="kickoffq-field-category"
            type="text"
            className={FIELD_CLASS}
            value={form.category}
            onChange={(e) => patch('category', e.target.value)}
          />,
        )}
        {labelledField(
          'kickoffq-field-text',
          'Question text',
          <textarea
            id="kickoffq-field-text"
            data-testid="kickoffq-field-text"
            rows={3}
            className={FIELD_CLASS}
            value={form.text}
            onChange={(e) => patch('text', e.target.value)}
          />,
        )}
        {labelledField(
          'kickoffq-field-ai',
          'AI instruction (optional)',
          <textarea
            id="kickoffq-field-ai"
            data-testid="kickoffq-field-ai"
            rows={2}
            className={`${FIELD_CLASS} font-mono`}
            value={form.aiInstruction}
            onChange={(e) => patch('aiInstruction', e.target.value)}
          />,
        )}
        {labelledField(
          'kickoffq-field-sort',
          'Sort order',
          <input
            id="kickoffq-field-sort"
            data-testid="kickoffq-field-sort"
            type="number"
            className={FIELD_CLASS}
            value={form.sortOrder}
            onChange={(e) => patch('sortOrder', e.target.value)}
          />,
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Question row
// ---------------------------------------------------------------------------

interface QuestionRowProps {
  readonly question: KickoffQuestionDTO;
  readonly onEdit: (q: KickoffQuestionDTO) => void;
  readonly onDelete: (q: KickoffQuestionDTO) => void;
}

function QuestionRow({ question, onEdit, onDelete }: QuestionRowProps): React.JSX.Element {
  const hint = (question.ai_instruction ?? '').trim();
  const hasHint = hint.length > 0;
  return (
    <div
      data-testid="kickoffq-row"
      data-question-id={question.id}
      className="group flex items-start justify-between gap-3 rounded-lg border border-border p-3"
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span data-testid="kickoffq-text" className="text-sm text-text-strong">
            {question.text}
          </span>
          {hasHint ? (
            <span
              data-testid="kickoffq-hint-badge"
              title={hint}
              className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium text-indigo-700"
            >
              hint
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 opacity-0 transition group-hover:opacity-100">
        <button
          type="button"
          data-testid="kickoffq-edit"
          onClick={() => onEdit(question)}
          className="text-xs text-gray-400 hover:text-primary"
          aria-label="Edit question"
        >
          <i className="fa-solid fa-pen" />
        </button>
        <button
          type="button"
          data-testid="kickoffq-delete"
          onClick={() => onDelete(question)}
          className="text-xs text-gray-400 hover:text-danger"
          aria-label="Delete question"
        >
          <i className="fa-solid fa-trash" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

/**
 * The Questionnaire admin tab. Lists active kickoff questions grouped by category
 * (first-appearance order) and opens the editor dialog for create / edit.
 */
export function QuestionnaireTab(): React.JSX.Element {
  const query = useKickoffQuestions();
  const toast = useToast();
  const deleteMut = useDeleteKickoffQuestion();
  // `editing === null` → no dialog; `editing === 'new'` → create; object → edit.
  const [editing, setEditing] = useState<KickoffQuestionDTO | 'new' | null>(null);

  const groups = useMemo(() => groupKickoffQuestionsByCategory(query.data ?? []), [query.data]);

  function handleDelete(q: KickoffQuestionDTO): void {
    deleteMut.mutate(q.id, {
      onSuccess: () => toast.success('Question deleted'),
      onError: (error) => toast.error(errorMessage(error, 'Delete error')),
    });
  }

  if (query.isLoading) {
    return (
      <div data-testid="kickoffq-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }
  if (query.isError) {
    return (
      <div data-testid="kickoffq-error" className="py-4 text-center text-red-500">
        {errorMessage(query.error, 'Failed to load questions')}
      </div>
    );
  }

  return (
    <div data-testid="kickoffq-tab">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-strong">Questionnaire (base questions)</h2>
        <button
          type="button"
          data-testid="kickoffq-add"
          onClick={() => setEditing('new')}
          className="rounded bg-primary px-3 py-2 text-sm text-surface hover:bg-primary-hover"
        >
          <i className="fa-solid fa-plus mr-1" /> Add question
        </button>
      </div>
      <p className="mb-4 text-xs text-gray-500">
        Base kick-off questionnaire template. Changes here affect all new projects. Per-question AI
        instructions guide the pre-fill AI.
      </p>

      {groups.length === 0 ? (
        <div data-testid="kickoffq-empty" className="py-4 text-center text-gray-400">
          No questions
        </div>
      ) : (
        <div data-testid="kickoffq-list" className="grid gap-6">
          {groups.map((group) => (
            <section
              key={group.category}
              data-testid="kickoffq-group"
              data-category={group.category}
            >
              <h3
                data-testid="kickoffq-group-title"
                className="mb-2 text-xs font-semibold tracking-wide text-gray-500"
              >
                {group.category}
              </h3>
              <div className="grid gap-2">
                {group.questions.map((q) => (
                  <QuestionRow
                    key={q.id}
                    question={q}
                    onEdit={(question) => setEditing(question)}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing !== null ? (
        <QuestionDialog
          question={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

export default QuestionnaireTab;
